import type { ColumnDef, RowData } from '@tanstack/react-table';
import { useMemo } from 'react';

/** Will derive the visible column names from the column definitions and the explicitly hidden columns. */
export function useDeriveVisibleColumns<T extends RowData>(
	columns: Array<ColumnDef<T>>,
	hiddenColumns: string[]
): Record<string, boolean> {
	return useDeriveColumns<T>(columns, columnName => !hiddenColumns.includes(columnName));
}

/** Will derive the hidden column names from the column definitions and the explicitly visible columns. */
export function useDeriveHiddenColumns<T extends RowData>(
	columns: Array<ColumnDef<T>>,
	visibleColumns: string[]
): Record<string, boolean> {
	return useDeriveColumns<T>(columns, columnName => visibleColumns.includes(columnName));
}

function useDeriveColumns<T extends RowData>(columns: Array<ColumnDef<T>>, deriveFn: (columnName: string) => boolean) {
	return useMemo(
		() =>
			columns
				.map(column => getColumnId(column))
				.reduce(
					(columnVisibility, columnName) => ({
						...columnVisibility,
						[columnName]: deriveFn(columnName)
					}),
					{}
				),
		[columns, deriveFn]
	);
}

/** Extracts the column ID/accessor key from the column definitions. Returns empty string if both are not found. */
export function getColumnId<T extends RowData>(column: ColumnDef<T>): string {
	if (column.id) {
		return column.id;
	}
	if ('accessorKey' in column) {
		return String(column.accessorKey);
	}
	return '';
}
